import React, { useContext, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { Context } from "../../../utils/context";
import { putData } from "../../../utils/api";
import ModalSave from "../../common/ModelSave";
import { CancelButton, SaveButton } from "../../common/Button";
import AnswerImage from "./AnswerImage";
import "../../Tabels/Tabels.css";

const toMessage = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map((v) => toMessage(v, "")).join(", ");
  return value.message || value.original?.sqlMessage || fallback;
};

const EditQuestion = (props) => {
  const id = props.show;
  const { getData } = useContext(Context);
  const [showModal, setShowModal] = useState({ code: 0, message: "" });
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      question: "",
      answers: [{ answer_id: null, answer: "", image: "", file: null }],
    },
  });

  // answer_id holds the saved answer's id (useFieldArray reserves "id" for itself),
  // image holds the saved image path and file a newly picked image
  const { fields, append, remove } = useFieldArray({
    control,
    name: "answers",
  });
  const answerValues = watch("answers");

  useEffect(() => {
    const loadQuestion = async () => {
      try {
        const response = await getData(`/admin/masters/question/${id}`);
        if (response?.data) {
          const answers = [...(response.data.answers || [])]
            .sort((a, b) => a.id - b.id)
            .map((item) => ({
              answer_id: item.id,
              answer: item.answer || "",
              image: item.image || "",
              file: null,
            }));
          reset({
            question: response.data.question || "",
            answers: answers.length
              ? answers
              : [{ answer_id: null, answer: "", image: "", file: null }],
          });
        }
      } catch (error) {
        console.error("Error loading question:", error);
        setShowModal({ code: 500, message: "Unable to load question" });
      }
    };
    loadQuestion();
  }, [getData, id, reset]);

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append("question", data.question.trim());
      // Saved answers go with their id (edit), new ones as plain text (create).
      // remove_image clears a saved image the admin removed.
      formData.append(
        "answers",
        JSON.stringify(
          data.answers.map((item) =>
            item.answer_id
              ? {
                  id: item.answer_id,
                  answer: item.answer.trim(),
                  remove_image: !item.image,
                }
              : item.answer.trim()
          )
        )
      );
      // Image of each answer goes under its position in the answers list
      data.answers.forEach((item, index) => {
        if (item.file?.[0]) {
          formData.append(`answers[${index}][image]`, item.file[0]);
        }
      });
      const response = await putData(`/admin/masters/question/${id}`, formData);

      setShowModal({
        code: response?.code || (response?.success ? 200 : 400),
        message: response?.success
          ? response.message
          : toMessage(
              response?.errors || response?.message,
              "Unable to update question"
            ),
      });

      if (response?.success) {
        setTimeout(() => {
          setShowModal({ code: 0, message: "" });
          props.handleClose();
        }, 1000);
      } else {
        setTimeout(() => setShowModal({ code: 0, message: "" }), 1500);
      }
    } catch (error) {
      console.error("Error updating question:", error);
      setShowModal({ code: 500, message: "Unable to update question" });
    }
  };

  return (
    <>
      <Modal
        {...props}
        onHide={props.handleClose}
        size="lg"
        aria-labelledby="question-edit-title"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="question-edit-title">Edit Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)} role="form">
            <Row>
              <Col md={12}>
                <Form.Group className="main-form-section mt-3">
                  <Form.Label>Question</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="e.g. What is your skin type?"
                      className={classNames({
                        "is-invalid": errors.question,
                      })}
                      {...register("question", {
                        required: "Question is required",
                        validate: (value) =>
                          value.trim() !== "" || "Question is required",
                        maxLength: {
                          value: 250,
                          message: "Question must be 250 characters or less",
                        },
                      })}
                    />
                  </InputGroup>
                  {errors.question && (
                    <span className="text-danger">
                      {errors.question.message}
                    </span>
                  )}
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="main-form-section mt-3">
                  <Form.Label>Answers</Form.Label>
                  {fields.map((field, index) => (
                    <div key={field.id} className="mb-3 border rounded p-2">
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder={`Answer ${index + 1}`}
                          className={classNames({
                            "is-invalid": errors.answers?.[index]?.answer,
                          })}
                          {...register(`answers.${index}.answer`, {
                            required: "Answer is required",
                            validate: (value) =>
                              value.trim() !== "" || "Answer is required",
                            maxLength: {
                              value: 250,
                              message: "Answer must be 250 characters or less",
                            },
                          })}
                        />
                        <Button
                          variant="outline-danger"
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </InputGroup>
                      {errors.answers?.[index]?.answer && (
                        <span className="text-danger">
                          {errors.answers[index].answer.message}
                        </span>
                      )}
                      <AnswerImage
                        inputProps={register(`answers.${index}.file`)}
                        file={answerValues?.[index]?.file?.[0]}
                        existingImage={answerValues?.[index]?.image}
                        onRemoveExisting={() =>
                          setValue(`answers.${index}.image`, "")
                        }
                      />
                    </div>
                  ))}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() =>
                      append({ answer_id: null, answer: "", image: "", file: null })
                    }
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add Answer
                  </Button>
                  <div className="text-muted small mt-2">
                    Removing a saved answer also removes it from every product
                    it is selected on.
                  </div>
                </Form.Group>
              </Col>
              <Row className="mt-5 pb-3">
                <div className="d-flex justify-content-center">
                  <CancelButton
                    name="cancel"
                    handleClose={props.handleClose}
                  />
                  <SaveButton
                    name="save"
                    handleSubmit={handleSubmit(onSubmit)}
                  />
                </div>
              </Row>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>
      <ModalSave
        message={showModal.message}
        showErrorModal={Boolean(showModal.code)}
      />
    </>
  );
};

export default EditQuestion;
